import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { Pricing, RentedSubscription, Subscription } from "common/types";

import { post } from "../helpers/HTTP";

async function workspaceEstimateUpdateRequest(
  workspaceId: string,
  subscriptionId: string,
  planId: string,
  addonId: string,
  quantity: number,
  prorate: boolean,
): Promise<{ amountDue: number; nextBillingAt: number }> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/estimates/update`,
    {
      items: [
        {
          id: planId,
        },
        ...(quantity > 0
          ? [
              {
                id: addonId,
                quantity,
              },
            ]
          : []),
      ],
      prorate,
    },
  );

  const {
    invoice_estimate: invoiceEstimate,
    subscription_estimate: subscriptionEstimate,
    next_invoice_estimate: nextInvoiceEstimate,
  } = estimate;

  return {
    amountDue: invoiceEstimate
      ? invoiceEstimate.amount_due
      : nextInvoiceEstimate.amount_due,
    nextBillingAt: subscriptionEstimate.next_billing_at,
  };
}

export default function useWorkspaceEstimateUpdate({
  subscription,
  pricing,
  quantity,
  prorate,
  isDisabled = false,
}: {
  subscription?: Subscription | RentedSubscription;
  pricing: { addon: Pricing; plan: Pricing };
  quantity: number;
  prorate: boolean;
  isDisabled?: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { addon, plan } = pricing;

  const { data: estimateUpdate, isLoading: isLoadingEstimateUpdate } = useQuery(
    {
      queryKey: [
        "estimateUpdate",
        workspaceId,
        subscription ? subscription.id : "no subscription",
        quantity,
        prorate,
      ],
      queryFn: () =>
        workspaceEstimateUpdateRequest(
          workspaceId,
          subscription?.id,
          plan.id,
          addon.id,
          quantity,
          prorate,
        ),
      enabled: !isDisabled && !!subscription, // The request should only be called if there is a subscription
    },
  );

  return { estimateUpdate, isLoadingEstimateUpdate };
}
